import { useEffect, useMemo, useState, useCallback } from 'react';
import PropTypes from 'prop-types';
import { useParams, useRouter } from 'src/routes/hook';
import { paths } from 'src/routes/paths';
import { localStorageAvailable } from 'src/utils/storage-available';
import { crossTabChannel, SiteConfigChangedMessage } from 'src/utils/cross-tab';
import { useCrossTabMessage } from 'src/hooks/use-cross-tab-message';
import { useSnackbar } from 'src/components/snackbar';
import { getSites } from '../../../cms-api/user_acl';
import { getList, get, update } from '../../../cms-api/site';
// import { getPageList } from '../../../cms-api/page';
import { SiteContext } from './site-context';
import { getSiteConfig } from './site-config';
import { SplashScreen } from '../../loading-screen';
import { createSiteConfig, getSiteData } from '../../../utils/site';
import { getAiSaver, updateTemplateDoc } from '../../../cms-api/ai';
import { useAuthContext } from '../../../auth/hooks';

// 一時保存の状態（読み込み直したタブが復元する）。別タブへの通知は src/utils/cross-tab。
const TEMP_SITE_CONFIG_SYNC_KEY = 'temp_site_config_sync';

export function SiteProvider({children}) {

  const router = useRouter();

  const params = useParams();
  const { site_code } = params;

  const [sites, setSites] = useState([]);

  const [site, setSiteState] = useState(null);

  const [siteConfig, setSiteConfig] = useState(null);
  
  const [tempSiteConfig, setTempSiteConfig] = useState(null);
  
  const [tempConfigHistory, setTempConfigHistory] = useState([]); // 一時保存の履歴配列
  const [historyIndex, setHistoryIndex] = useState(-1); // 履歴内の現在位置
  
  const [hasTempChanges, setHasTempChanges] = useState(false);

  const [loading, setLoading] = useState(true);
  
  const { enqueueSnackbar } = useSnackbar();
  
  const { user } = useAuthContext();

  // ---- 別タブとの同期 ----
  // 同じサイトを開いている別タブに「設定が変わった」を知らせ、受けたタブは読み込み直す。
  // 運び方はアプリ共通の src/utils/cross-tab（以前は localStorage の
  // site_config_reload / last_config_change_timestamp を storage イベントで見ていた）。
  const notifySiteConfigChanged = useCallback(() => {
    if (site) crossTabChannel.post(new SiteConfigChangedMessage({ siteId: site.site_id }));
  }, [site]);

  useCrossTabMessage(SiteConfigChangedMessage, (message) => {
    if (site && message.siteId === site.site_id) window.location.reload();
  });

  // 一時保存の状態を localStorage に残し（読み込み直したタブが復元する）、別タブへ知らせる。
  // ⚠ 残すのが先、知らせるのが後。受けたタブは読み込み直した先でこの値を読む。
  const syncTempConfig = useCallback((tempState) => {
    if (!site) return;
    if (localStorageAvailable()) {
      localStorage.setItem(TEMP_SITE_CONFIG_SYNC_KEY, JSON.stringify({
        siteId: site.site_id,
        tempConfig: tempState.tempConfig,
        tempHistory: tempState.tempHistory,
        historyIndex: tempState.historyIndex,
        hasTempChanges: tempState.hasTempChanges,
      }));
    }
    notifySiteConfigChanged();
  }, [site, notifySiteConfigChanged]);

  const updateSiteConfig = useCallback((newConfig) => {
    setSiteConfig(newConfig);
    if (newConfig) notifySiteConfigChanged();
  }, [notifySiteConfigChanged]);

  const setSite = useCallback(async (newSite) => {

    setLoading(true);

    const oldSiteId = localStorage.getItem('site_id');

    let isPush = false;
    if (!newSite) {
      isPush = true;
    } else if (!oldSiteId) {
      isPush = true;
    } else if (newSite.site_id !== oldSiteId) {
      isPush = true;
    }

    if (newSite) {
      localStorage.setItem('site_id', newSite.site_id);

      const { siteData, configData } = await getSiteData(newSite.site_id);

      setSiteState(siteData);
      setSiteConfig(configData);
      setLoading(false);

    } else {
      localStorage.removeItem('site_id');
      setSiteState(null);
      setSiteConfig({});
      setLoading(false);
    }

    if (isPush) {
      if (newSite) {
        router.push(paths.dashboard.site.root(newSite.code));
      } else {
        // router.push(paths.dashboard.root);
      }
    }

  }, [router]);

  const saveTempConfig = useCallback((newConfig) => {
    const newHistory = historyIndex >= 0 
      ? tempConfigHistory.slice(0, historyIndex + 1)
      : [];
    
    const currentPath = window.location.pathname;
    
    const historyItem = {
      config: newConfig,
      pagePath: currentPath
    };
    
    const updatedHistory = [...newHistory, historyItem];
    
    const MAX_HISTORY_SIZE = 10;
    if (updatedHistory.length > MAX_HISTORY_SIZE) {
      updatedHistory.shift(); // 最も古い履歴を削除
    }

    setTempConfigHistory(updatedHistory);
    setHistoryIndex(updatedHistory.length - 1);
    setTempSiteConfig(newConfig);
    setHasTempChanges(true);

    syncTempConfig({
      tempConfig: newConfig,
      tempHistory: updatedHistory,
      historyIndex: updatedHistory.length - 1,
      hasTempChanges: true,
    });

    enqueueSnackbar('設定を一時保存しました。適用するには「適用」ボタンをクリックしてください。', { variant: 'success' });
  }, [enqueueSnackbar, tempConfigHistory, historyIndex, syncTempConfig]);

  const getTempConfig = useCallback(() => tempSiteConfig || siteConfig, [tempSiteConfig, siteConfig]);

  const applyConfig = useCallback(async () => {
    if (!site) {
      return;
    }

    const { siteData, configData } = await getSiteData(site.site_id);

    setSiteState(siteData);
    setSiteConfig(configData);
  }, [site, setSiteState, setSiteConfig]);

  const applyTempConfig = useCallback(async () => {
    if (!tempSiteConfig || !site) {
      return;
    }

    try {

      const json_data_str = JSON.stringify(tempSiteConfig)

      const { siteData } = await getSiteData(site.site_id);
      
      siteData.expand_value.text.private_sys_admin_config = json_data_str;
      
      const sendData = {
        name: siteData.name,
        domain: siteData.domains[0],
        expand_value: siteData.expand_value,
        site_id: site.site_id,
      };
      
      await update(sendData);

      try {
        const updatedVersion = siteData.expand_value.text.private_sys_admin_config_version;
        const saverData = await getAiSaver();
        await updateTemplateDoc({
          address: saverData.address,
          site_id: site.site_id,
          version: updatedVersion,
        });
        console.log('Template doc update requested successfully');
      } catch (templateDocError) {
        console.error('Error requesting template doc update:', templateDocError);
      }
      
      // 通知は下の syncTempConfig が 1 回だけ出す（一時保存の状態を残してから）。
      setSiteConfig(tempSiteConfig);
      setHasTempChanges(false);
      
      // setTempConfigHistory([]);
      // setHistoryIndex(-1);
      
      syncTempConfig({
        tempConfig: tempSiteConfig,
        tempHistory: tempConfigHistory,
        historyIndex,
        hasTempChanges: false, // 適用後は変更なしの状態
      });

      applyConfig();
      
      enqueueSnackbar('設定を適用しました', { variant: 'success' });
    } catch (error) {
      console.error('Error applying temporary configuration:', error);
      enqueueSnackbar('設定の適用に失敗しました', { variant: 'error' });
    }
  }, [tempSiteConfig, site, enqueueSnackbar, tempConfigHistory, historyIndex, applyConfig, syncTempConfig]);

  useEffect(() => {
    getSites().then((res) => {
      if (res.site_ids?.length > 0) {
        getList({
          site_ids: res.site_ids,
        }).then((resAll) => {
          setSites(resAll.site_list);

          if (!site) {
            if (site_code) {
              const getSite = resAll.site_list.find((fsite) => fsite.code === site_code);
              setSite(getSite);
            } else if (resAll.site_list.length > 0) {
              const defSiteId = localStorage.getItem('site_id')

              if (defSiteId) {
                const defSite = resAll.site_list.find((fsite) => fsite.site_id === defSiteId);
                if (defSite) {
                  // setSite(defSite);
                  router.push(paths.dashboard.site.root(defSite.code));
                } else {
                  // setSite(resAll.site_list[0])
                  router.push(paths.dashboard.site.root(resAll.site_list[0].code));
                }
              } else {
                router.push(paths.dashboard.site.root(resAll.site_list[0].code));
                // setSite(resAll.site_list[0])
              }

            } else if (resAll.site_list.length === 0) {
              setSite(null)
            }
          }
        });
      } else {
        setSite(null)
      }
    });
  }, [site, setSite, setSiteConfig, site_code, router])

  const getPreviousTempConfig = useCallback(() => {
    if (historyIndex > 0) {
      const newIndex = historyIndex - 1;
      setHistoryIndex(newIndex);
      
      const historyItem = tempConfigHistory[newIndex];
      const config = historyItem.config || historyItem;
      
      setTempSiteConfig(config);
      
      if (historyItem.pagePath && historyItem.pagePath !== window.location.pathname) {
        router.push(historyItem.pagePath);
      }
      
      syncTempConfig({
        tempConfig: config,
        tempHistory: tempConfigHistory,
        historyIndex: newIndex,
        hasTempChanges: true,
      });

      return config;
    }
    return tempSiteConfig;
  }, [historyIndex, tempConfigHistory, tempSiteConfig, router, syncTempConfig]);

  const getConfigEditData = useCallback(() => {
    if (!site) return null;

    if (hasTempChanges) {
      const tmpConfig = getTempConfig()

      if (tmpConfig) {
        return JSON.parse(JSON.stringify(tmpConfig))
      }
    }

    const { configData } = createSiteConfig(site);

    return JSON.parse(JSON.stringify(configData))
  }, [site, getTempConfig, hasTempChanges]);

  const getNextTempConfig = useCallback(() => {
    if (historyIndex < tempConfigHistory.length - 1) {
      const newIndex = historyIndex + 1;
      setHistoryIndex(newIndex);
      
      const historyItem = tempConfigHistory[newIndex];
      const config = historyItem.config || historyItem;
      
      setTempSiteConfig(config);
      
      if (historyItem.pagePath && historyItem.pagePath !== window.location.pathname) {
        router.push(historyItem.pagePath);
      }
      
      syncTempConfig({
        tempConfig: config,
        tempHistory: tempConfigHistory,
        historyIndex: newIndex,
        hasTempChanges: true,
      });

      return config;
    }
    return tempSiteConfig;
  }, [historyIndex, tempConfigHistory, tempSiteConfig, router, syncTempConfig]);

  const hasPreviousTempConfig = useCallback(() => historyIndex > 0, [historyIndex]);

  const hasNextTempConfig = useCallback(() => historyIndex < tempConfigHistory.length - 1, [historyIndex, tempConfigHistory]);

  const clearTempConfig = useCallback(() => {
    const tempHistory = siteConfig ? [{ config: siteConfig, pagePath: window.location.pathname }] : [];
    const index = siteConfig ? 0 : -1;
    setTempSiteConfig(siteConfig);
    setTempConfigHistory(tempHistory);
    setHistoryIndex(index);
    setHasTempChanges(false);

    syncTempConfig({
      tempConfig: siteConfig,
      tempHistory,
      historyIndex: index,
      hasTempChanges: false,
    });

    enqueueSnackbar('一時保存の設定変更をクリアしました', { variant: 'success' });
  }, [siteConfig, enqueueSnackbar, syncTempConfig]);

  useEffect(() => {
    if (siteConfig) {
      const storageAvailable = localStorageAvailable();
      if (storageAvailable) {
        const tempDataStr = localStorage.getItem(TEMP_SITE_CONFIG_SYNC_KEY);
        if (tempDataStr) {
          try {
            const tempData = JSON.parse(tempDataStr);
            if (tempData.siteId === site?.site_id) {
              setTempSiteConfig(tempData.tempConfig);
              setTempConfigHistory(tempData.tempHistory);
              setHistoryIndex(tempData.historyIndex);
              setHasTempChanges(tempData.hasTempChanges);
              return; // 既存データがあれば初期化をスキップ
            }
          } catch (error) {
            console.error('Error parsing temp site config sync data:', error);
          }
        }
      }
      
      setTempSiteConfig(siteConfig);
      setTempConfigHistory(siteConfig ? [{ config: siteConfig, pagePath: window.location.pathname }] : []);
      setHistoryIndex(siteConfig ? 0 : -1);
    }
  }, [siteConfig, site]);

  const memoizedValue = useMemo(
    () => ({
      site,
      siteConfig,
      tempSiteConfig,
      tempConfigHistory,
      historyIndex,
      hasTempChanges,
      sites,
      loading,
      setSite,
      setSiteConfig: updateSiteConfig,
      saveTempConfig,
      applyTempConfig,
      getTempConfig,
      clearTempConfig,
      getPreviousTempConfig,
      getNextTempConfig,
      hasPreviousTempConfig,
      hasNextTempConfig,
      getConfigEditData,
      setSiteState,
      applyConfig,
    }),
    [site, sites, loading, setSite, siteConfig,
      tempSiteConfig, tempConfigHistory, historyIndex,
      hasTempChanges, updateSiteConfig, saveTempConfig,
      applyTempConfig, getTempConfig, clearTempConfig,
      getPreviousTempConfig, getNextTempConfig, hasPreviousTempConfig,
      hasNextTempConfig, getConfigEditData, setSiteState, applyConfig]
  );

  return <SiteContext.Provider value={memoizedValue}>{ loading ? <SplashScreen /> : children }</SiteContext.Provider>;
}

SiteProvider.propTypes = {
  children: PropTypes.node,
};
